/* eslint-disable tailwindcss/no-custom-classname */
import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useRouter } from 'next/router';
import app from '@frontastic/catwalk/src/js/app/app';
import { useDeviceType } from '@frontastic/catwalk/src/js/helper/hooks/useDeviceType';
import debounce from '@frontastic/common/src/js/helper/debounce';
import StoreService from 'anwr_sport2000/src/js/services/algolia/StoreService';
import PriceHelper from 'anwr_sport2000/src/js/services/price';
import classnames from 'classnames';
import _cloneDeep from 'lodash/cloneDeep';
import _isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { useCookies } from 'react-cookie';
import { Link, useLocation } from 'react-router-dom';
import ProductData from './ProductData';
import ProductImages from './ProductImages';
import ProductInfo from './ProductInfo/ProductInfo';
import ProductSchemaSeo from './ProductSchemaSeo';
import useConsentManagement from '../../../../../legacy/js/services/hook/useConsentManagement';
import OldTagManager from '../../../domain/OldTagManager';
import TagManager from '../../../domain/TagManager';
import GtmService from '../../../services/gtm';
import OldGtmService from '../../../services/oldGtm';
import { addOrUpdateQueryParam, getQueryParam } from '../../../utils/helpers/urlHelper';
import ProductTags from '../../atoms/tag/productTags';
import PdpCampaign from '../GraphCMS/ProductDetailCampaign';
import ProductRecommendationList from '../productRecommendationList';
import Image from 'next/image';
import useIsBot from 'helpers/hooks/useIsBot';

const ProductDetails = ({
  productDefault,
  colorProducts,
  onChangeColorProducts,
  handleAddToCart,
  productColor,
  selectedVariant,
  onChangeVariantBySku,
  refererStore,
  productRecommendationData,
  productBrandRecommendationData,
  productStoreRecommendationData,
  algoliaIndexName,
  graphCampaign,
  textColor,
  bgColor,
  positionMode,
  alignText,
  onOpenOffCanvasStoreFinder,
  gtmCategoryName,
  gtmCategoryId,
  productBrand,
  context,
}) => {
  const router = useRouter();
  const intervalId = useRef(null);
  const { isGtmAccepted } = useConsentManagement();
  const isMobile = useDeviceType() === 'mobile';
  const location = useLocation();

  const [isTrackedQueryImpression, setIsTrackedQueryImpression] = useState(false);

  const { runningCategoryId } = app.getApi().router.context.projectConfiguration;
  const { absoluteCategoryId } = app.getApi().router.context.projectConfiguration;

  const productCategoryIds = productColor?.projectSpecificData?.categoryIds || [];
  const [cookie] = useCookies();
  const { store } = cookie;
  const mainStore = refererStore || store || '';

  const defaultIndexRunningCategory = productCategoryIds.findIndex((category) => category === runningCategoryId) > -1;
  const defaultIndexAbsoluteCategory = productCategoryIds.findIndex((category) => category === absoluteCategoryId) > -1;

  const [productTags, setProductTags] = useState(selectedVariant?.attributes?.att_internal_category_sport2000 || []);
  const [queryId, setQueryId] = useState('');

  // Get product attributes series and filter to get GraphCMS
  const attributeProductSeries = productColor?.variants[0]?.attributes.att_prod_product_series || '';
  const filterArrayCampaign =
    graphCampaign &&
    graphCampaign.filter((data) => data?.adentifierattribut[0]?.toLowerCase() === attributeProductSeries.toLowerCase());
  const productGraphCampaign = filterArrayCampaign && filterArrayCampaign.length > 0 && filterArrayCampaign[0];
  const brandName = selectedVariant?.attributes?.att_prod_brand;
  const brandUrl = selectedVariant?.attributes?.att_prod_brand_url;
  const currentUrl = location.asPath;
  const productAttributeVisualization = useMemo(() => productDefault?.projectSpecificData?.attributesVisualization, [
    productDefault?.projectSpecificData?.attributesVisualization,
  ]);

  const handleAddToCartGTM = () => {
    const gtmService = new GtmService(null, selectedVariant);
    const productCategories = productDefault?.projectSpecificData?.categories || productColor?.projectSpecificData?.categories || [];
    const currentCategory = productCategories?.[productCategories.length - 1]
    const productGTM = gtmService.createProductGTM('Product Detail', {
      quantity: 1,
      query_id: queryId,
    }, {}, currentCategory);
    TagManager.addProduct(productGTM);

    // for universal analytics ( will be removed )
    const oldGtmService = new OldGtmService(null, selectedVariant);
    const oldProductGtm = oldGtmService.createProductWithQuantityGTM({
      query_id: queryId,
    });
    OldTagManager.addProduct(oldProductGtm);
  };

  const handleUpdateSkuToUrl = (sku) => {
    const currentQueryParams = getQueryParam(currentUrl);
    const productColorUrl = productColor?._url;
    const fullProductColorUrl = addOrUpdateQueryParam(productColorUrl, {
      ...currentQueryParams,
      sku,
      queryId,
    });
    window.history.replaceState(null, '', fullProductColorUrl);
  };

  const stickyJProductInformation = () => {
    const targetDiv = document.getElementById('product--detail--data');

    if (targetDiv && document.documentElement.scrollTop >= targetDiv.offsetTop) {
      document.body.classList.add('is--sticky');
    }

    if (targetDiv && document.documentElement.scrollTop < targetDiv.offsetTop) {
      document.body.classList.remove('is--sticky');
    }
  };

  useEffect(() => {
    if (defaultIndexAbsoluteCategory) {
      document.body.classList.add('absolute-teamsport--theme');
    } else {
      document.body.classList.remove('absolute-teamsport--theme');
    }

    if (defaultIndexRunningCategory) {
      document.body.classList.add('absolute-run--theme');
    } else {
      document.body.classList.remove('absolute-run--theme');
    }
  });

  useEffect(() => {
    const variantSelectedCheckedStockInStore = StoreService.checkProductStockInStore(mainStore, selectedVariant);
    const isSelectedVariantOnSale = PriceHelper.isDiscountOverThreshold(
      variantSelectedCheckedStockInStore?.discountedPrice || 0,
      variantSelectedCheckedStockInStore?.price || 0,
    );

    if (isSelectedVariantOnSale) {
      setProductTags((curProductTag) => [...curProductTag, 'sale']);
    } else {
      setProductTags(selectedVariant?.attributes?.att_internal_category_sport2000 || []);
    }
  }, [selectedVariant, refererStore, cookie, mainStore]);

  useEffect(() => {
    intervalId.current = setInterval(() => {
      if (isGtmAccepted && window.aa && !isTrackedQueryImpression && algoliaIndexName && selectedVariant) {
        TagManager.queryIdImpression([selectedVariant.sku], algoliaIndexName);
        setIsTrackedQueryImpression(true);
      }
    }, 500);

    if (isTrackedQueryImpression) {
      clearInterval(intervalId.current);
    }

    return () => clearInterval(intervalId.current);
  }, [isGtmAccepted, selectedVariant, algoliaIndexName, isTrackedQueryImpression]);

  useEffect(() => {
    setQueryId(getQueryParam(currentUrl, 'queryId'));
    window.addEventListener(
      'scroll',
      debounce(() => stickyJProductInformation(), 25),
      { passive: true },
    );

    return window.removeEventListener(
      'scroll',
      debounce(() => stickyJProductInformation(), 25),
    );
  }, [currentUrl]);

  const isBot = useIsBot()
  const isComeFromStore = !!refererStore || !!router.query.objectId;

  const hasProductRecommendation = !isBot && !!productRecommendationData && !isComeFromStore;
  const hasProductBrandRecommendation = !isBot && !!productBrandRecommendationData && !isComeFromStore;
  const hasProductStoreRecommendation =
    !isBot && isComeFromStore && !!refererStore && !!productStoreRecommendationData;

  const videoProductAttribute = useMemo(
    () => selectedVariant?.attributes?.att_txt_long_product_video_links,
    [selectedVariant],
  );
  const productImagesSlider = useMemo(
    () => (videoProductAttribute ? [...selectedVariant?.images, videoProductAttribute] : selectedVariant?.images),
    [selectedVariant?.images, videoProductAttribute],
  );

  const renderProductData = () => (
    <div className={'product-detail-data-wrapper'} id={'product--detail--data'}>
      <div className={'product-detail-data'}>
        <ProductData
          colorProducts={_cloneDeep(colorProducts)}
          onChangeColorProducts={onChangeColorProducts}
          variants={productColor.variants}
          selectedVariant={selectedVariant}
          onChangeSelectedVariant={onChangeVariantBySku}
          handleAddToCart={handleAddToCart}
          onUpdateSkuToUrl={handleUpdateSkuToUrl}
          brandSizeTable={productBrand?.urlSizeTable}
          onTrackPurchaseGTM={handleAddToCartGTM}
          refererStore={refererStore}
          onOpenOffCanvasStoreFinder={onOpenOffCanvasStoreFinder}
          queryId={queryId}
          context={context}
        />
      </div>
    </div>
  );

  if (_isEmpty(productColor) || _isEmpty(selectedVariant)) {
    return null;
  }

  return (
    <div className={'product-detail-wrapper'}>
      <ProductSchemaSeo productColor={productColor} selectedVariant={selectedVariant} refererStore={refererStore} />
      {selectedVariant.sku && algoliaIndexName && (
        <div
          className={'product-detail-component lg:flex'}
          data-insights-object-id={selectedVariant.sku}
          data-insights-index={algoliaIndexName}
        >
          <div className={'product-detail--wrapper'}>
            <div className={'product-detail-image-wrapper'}>
              {productBrand?.logo && (
                <div className={'product-detail-image-logo'}>
                  <Link itemProp={'url'} to={brandUrl}>
                    <Image
                      loader={({ src }) => `${src}`}
                      src={productBrand.logo}
                      alt={brandName}
                      width={130}
                      height={55}
                      objectFit="contain"
                      objectPosition="center"
                      loading='lazy'
                    />
                  </Link>
                </div>
              )}
              {isMobile && <ProductTags tags={productTags} className={'product-detail-tags'} />}
              {selectedVariant && selectedVariant.images ? (
                <ProductImages images={productImagesSlider} title={productColor.name} />
              ) : (
                <div className={'product-detail--placeholder'} />
              )}
            </div>

            <div className={'product-data-mobile lg:hidden'}>{renderProductData()}</div>

            <div
              className={classnames('product-detail-feature', {
                'dark-mode':
                  selectedVariant.attributes.att_internal_category_absolute_fitness ||
                  selectedVariant.attributes.att_internal_category_absolute_outdoor ||
                  selectedVariant.attributes.att_internal_category_absolute_run ||
                  selectedVariant.attributes.att_internal_category_absolute_teamsport,
              })}
            >
              <ProductInfo
                productColor={productColor}
                selectedVariant={selectedVariant}
                productBrand={productBrand}
                productAttributeVisualization={productAttributeVisualization}
              />
            </div>
            {productGraphCampaign && (
              <PdpCampaign
                data={productGraphCampaign}
                color={textColor}
                backgroundColor={bgColor}
                position={positionMode}
                alignText={alignText}
              />
            )}
            {!!hasProductRecommendation && (
              <ProductRecommendationList
                {...productRecommendationData}
                selectedVariant={selectedVariant}
                brandName={brandName}
                brandUrl={brandUrl}
                isDisplayOnPDP
                gtmCategoryName={gtmCategoryName}
                gtmCategoryId={gtmCategoryId}
              />
            )}
            {hasProductBrandRecommendation && (
              <ProductRecommendationList
                {...productBrandRecommendationData}
                selectedVariant={selectedVariant}
                brandName={brandName}
                brandUrl={brandUrl}
                isDisplayOnPDP
                gtmCategoryName={gtmCategoryName}
                gtmCategoryId={gtmCategoryId}
              />
            )}
            {hasProductStoreRecommendation && (
              <ProductRecommendationList
                {...productStoreRecommendationData}
                selectedVariant={selectedVariant}
                refererStore={refererStore}
                isDisplayOnPDP
                gtmCategoryName={gtmCategoryName}
                gtmCategoryId={gtmCategoryId}
              />
            )}
          </div>

          <div className="product-data-desktop hidden lg:block">{renderProductData()}</div>
        </div>
      )}
    </div>
  );
};

ProductDetails.propTypes = {
  colorProducts: PropTypes.array.isRequired,
  handleAddToCart: PropTypes.func.isRequired,
  onChangeColorProducts: PropTypes.func.isRequired,
  productDefault: PropTypes.any,
  setIsOpenStoreSidebar: PropTypes.any,
  productColor: PropTypes.object,
  selectedVariant: PropTypes.any,
  onChangeVariantBySku: PropTypes.any,
  productBrandRecommendationData: PropTypes.any,
  productStoreRecommendationData: PropTypes.any,
  refererStore: PropTypes.any,
  context: PropTypes.any,
  onOpenOffCanvasStoreFinder: PropTypes.any,
  productRecommendationData: PropTypes.any,
  algoliaIndexName: PropTypes.string,
  bgColor: PropTypes.string,
  textColor: PropTypes.string,
  alignText: PropTypes.oneOf(['left', 'right', 'center', 'justify']),
  positionMode: PropTypes.PropTypes.oneOf(['left', 'right']),
  graphCampaign: PropTypes.object,
  productBrand: PropTypes.any,
};

export default ProductDetails;
